import React, { useCallback, useState, useMemo } from "react";
import { Grid, Typography, styled, Box } from "@mui/material";
import classNames from "classnames";
import { useTheme } from "@mui/styles";
import { IFlightType, IFlightAircraft, IGroundEvent, IFlight } from "@vmosolutions/oc-interfaces";
import { invertHexColor } from "src/pages/FlightTimeline/TimelineUtils";
import { v1 } from "uuid";
import { LightenDarkenColor, milli2DaysHrsMin } from "src/utils/index";
import { useDrag } from "react-dnd";
import { ChipContextMenu } from "./ChipContextMenu";
import { setHoveredEvent, setFocusedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import { useDispatch } from "react-redux";
export interface IEventChip {
  _id?: string;
  location?: string;
  aircraft?: IFlightAircraft;
  eventType?: IFlightType;
  actualStart?: number | Date;
  actualEnd?: number | Date;
}

export interface IEventChipCollectionProps {
  events: IGroundEvent[];
  displayTotal?: number;
  disableFocus?: boolean;
  disableContext?: boolean;
}

interface IEventChipProps {
  data?: IEventChip;
  className?: string;
  onClickChip?: (eventId: string) => void;
  disableFocus?: boolean;
  disableContext?: boolean;
  showDuration?: boolean;
}

interface IGroupedEventChipProps {
  data?: IGroundEvent[];
}

const OCEventChip = styled(Grid)(({ theme }) => ({
  userSelect: "none",
  width: 120,
  height: 26,
  boxShadow: "2px 2px 5px " + theme.palette.common.black,
  justifyContent: "space-between",
  display: "flex",
  flexDirection: "row",
  margin: "2px",
  borderRadius: "2px",
  fontFamily: "B612 Mono, arial, sans-serif", // Cousine showed poor anti-aliasing (blurred)
  fontSize: 10,
  fontWeight: "normal",
  padding: "5px",
  alignItems: "center",
  color: theme.palette.primary.contrastText, // This get's overriden by the inline style applied to assign best contrast color.
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shortest,
  }),
  overflow: "hidden",
  position: "relative",
  "&.cancelled": {
    opacity: 0.75,
    userSelect: "none",
    "& .cancelled_icon": {
      marginLeft: "-5px",
    },
  },
  "&.status_bar": {
    paddingTop: "2px",
    paddingBottom: "3px",
    boxShadow: "1px 1px 2px " + theme.palette.common.black,
    "& .origin, & .destination , & .flightnumber, & .event_title": {
      height: "auto",
      marginTop: "3px",
    },
  },
  "&.draggable": {
    cursor: "grab",
    "&:active": { cursor: "grabbing" },
  },
  "& div": {
    display: "flex",
    flexDirection: "row",
    padding: "0px 5px",
  },
  "& .details": {
    flexDirection: "column",
    "& div": { lineHeight: "9px", padding: 0 },
  },
  "& .aircraft_icon": {
    marginTop: "-2px",
    transform: "rotate(50deg) scale(1.4)",
    opacity: 0.2,
    "& path": { fill: "black" },
  },
  "& .info": {
    position: "absolute",
    width: 110,
    "& .flightnumber, & .event_title": {
      fontSize: 12,
      fontWeight: "bold",
      padding: "0px 2px",
    },
    "& .origin, & .destination ": {
      padding: "0px 2px",
    },
  },
  "&.interactive": {
    "&:hover": { margin: "2px 0px", width: 124, cursor: "pointer" },
    "&:active": { margin: "2px 4px", width: 116 },
  },
}));

export const EventChip = (props: IEventChipProps): JSX.Element => {
  const { data, className, onClickChip, disableContext, disableFocus, showDuration } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const [contextAnchor, setContextAnchor] = useState(null);

  const duration = useMemo(() => {
    if (showDuration) {
      const dif = data?.actualEnd?.valueOf() - data?.actualStart.valueOf();
      return dif ? milli2DaysHrsMin(dif) : null;
    }
    return null;
  }, [data?.actualEnd, data?.actualStart, showDuration]);

  const dragType: string = useMemo(
    () =>
      data?.eventType?.isGroundTurnBack || data?.eventType?.isCrewable || data?.eventType?.isSim ? "GROUND_EVENT" : "",
    [data]
  );

  const [collected, drag, dragPreview] = useDrag(
    {
      type: dragType,
      item: { groundEvent: data, type: dragType },
    },
    [dragType, data]
  );
  // Event has ID
  const assignedEvent: boolean = !!data?._id && !data._id.includes("temp");

  // Set context menu anchor (opens it);
  const handlePopoverOpen = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    event.preventDefault();
    event.stopPropagation(); //! Ensure right click doesn't select if used in table row.
    setContextAnchor(event.currentTarget);
  }, []);

  const handleFocusEvent = useCallback(
    (data: IFlight | IGroundEvent): void => {
      dispatch(setFocusedEvents({ events: [data], shouldFocus: "first" }));
    },
    [dispatch]
  );

  // Fire off onClickChip if supplied by properties in valid event and not disabled
  const handleClickChip = useCallback(
    (event: React.MouseEvent): void => {
      if (assignedEvent && onClickChip && typeof onClickChip === "function" && !disableFocus) {
        event.stopPropagation(); // important when used in the table where rows are clickable.
        onClickChip(data?._id);
      }
    },
    [data?._id, assignedEvent, onClickChip, disableFocus]
  );

  if (data) {
    const { _id, aircraft, location, eventType } = data;
    const eventId = _id ? _id : v1();

    if (!aircraft || !location || !eventType) {
      return null;
    }
    const { color } = eventType;

    // If all required data is passed, render the event chip.
    const chipClass = eventId + "-eventChip";
    const frColor = color ? (assignedEvent ? invertHexColor(color) : `#${color}`) : "blue";
    const bgColor1 = color ? (assignedEvent ? `#${color}` : invertHexColor(color)) : "white";
    const bgColor2 = LightenDarkenColor(bgColor1, -0.15);

    // Create diagonal background effect matching the one used in the webgl timeline.
    const backgroundEffect = `repeating-linear-gradient(135deg, ${bgColor1}, ${bgColor1} 10px, ${bgColor2} 10px, ${bgColor2} 20px)`;
    const borderStyle = color ? (assignedEvent ? "none" : `1px dashed #${color}`) : "1px solid white";

    return (
      <OCEventChip
        ref={drag && assignedEvent ? drag : null}
        item
        id={eventId}
        key={eventId}
        style={{
          color: frColor,
          background: backgroundEffect,
          border: borderStyle,
          width: showDuration ? "140px" : "120px",
        }}
        // Apply interactive components only if id is passed in.
        className={classNames(className, "eventchip", !!onClickChip && assignedEvent && "interactive", chipClass)}
        onMouseEnter={() => {
          if (assignedEvent) dispatch(setHoveredEvent(data as IGroundEvent));
        }}
        onMouseLeave={() => {
          if (assignedEvent) dispatch(setHoveredEvent(null));
        }}
        onClick={handleClickChip}
        onContextMenu={handlePopoverOpen}
      >
        <Grid item className="event_title" style={{ backgroundColor: bgColor1 }}>
          {eventType.title}
          {showDuration && <Box>{duration}</Box>}
        </Grid>
        <Grid item className="details" style={{ backgroundColor: bgColor1 }}>
          <div className="aircraft">{aircraft.registration}</div>
          <div className="location">{location}</div>
        </Grid>
        {!disableContext && assignedEvent && (
          <ChipContextMenu event={data as IGroundEvent} anchorEl={contextAnchor} setAnchor={setContextAnchor} />
        )}
      </OCEventChip>
    );
  }
  return null;
};

/**
 * Shared component that can be used to display a specific count of selected flight chips.
 * @param props -
 */
export const EventChipCollection = (props: IEventChipCollectionProps): JSX.Element => {
  const { events, displayTotal, disableFocus, disableContext } = props;
  const totalShown = displayTotal ?? 20;
  return (
    <Grid container direction={"row"} justifyContent={"center"}>
      {events.map((selectedEvent: IGroundEvent, index: number) => (
        <Grid item key={index}>
          {index < totalShown && (
            <EventChip data={selectedEvent} disableContext={disableContext} disableFocus={disableFocus} />
          )}
          {events.length >= totalShown && index === totalShown - 1 && (
            <Typography
              variant="body2"
              color="textPrimary"
              style={{ margin: 5, textAlign: "right" }}
            >{`...${events.length} total events`}</Typography>
          )}
        </Grid>
      ))}
    </Grid>
  );
};

export const GroupedEventChip = (props: IGroupedEventChipProps): JSX.Element => {
  const { data } = props;

  const color = "0f7b89";
  const frColor = "white";
  const bgColor1 = `#${color}`;
  const bgColor2 = LightenDarkenColor(bgColor1, -0.15);

  // Create diagonal background effect matching the one used in the webgl timeline.
  const backgroundEffect = `repeating-linear-gradient(135deg, ${bgColor1}, ${bgColor1} 10px, ${bgColor2} 10px, ${bgColor2} 20px)`;
  const borderStyle = `1px dashed ${bgColor1}`;

  const totalDuration = useMemo(() => {
    let time = 0;
    data.forEach((event) => {
      const duration = event.actualEnd.valueOf() - event.actualStart.valueOf();
      time += duration;
    });
    return time;
  }, [data]);

  return (
    <OCEventChip
      style={{
        color: frColor,
        background: backgroundEffect,
        border: borderStyle,
        width: "160px",
      }}
    >
      <Grid item className="event_title">
        {data.length} {data.length === 1 ? "Event" : "Events"}
      </Grid>
      <Grid item className="details">
        <div className="aircraft">Duration</div>
        <div className="location">{milli2DaysHrsMin(totalDuration)}</div>
      </Grid>
    </OCEventChip>
  );
};
